import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { isRunningStandalone, isPWAInstallable } from './utils/pwaInstall';
import NetworkStatusIndicator from './components/NetworkStatusIndicator';
import InstallButton from './components/InstallButton';
import HomePage from './pages/HomePage';
import Quiz from './components/Quiz';
import './App.css';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#fff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
  },
});

function App() {
  // State for PWA installation prompt
  const [showInstallMessage, setShowInstallMessage] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(isRunningStandalone());

  // Handle beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      
      // Only show the install button if not in standalone mode
      if (!isRunningStandalone()) {
        setShowInstallMessage(true);
      }
      
      // Log analytics event
      console.log('PWA install prompt triggered');
    };

    // Handle app installation
    const handleAppInstalled = () => {
      console.log('App was installed');
      setIsAppInstalled(true);
      setShowInstallMessage(false);
    };

    // Only add the event listener if PWA is installable
    if (isPWAInstallable()) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
    }

    return () => {
      if (isPWAInstallable()) {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      }
    };
  }, []);

  // Add a class to the body when in standalone mode
  useEffect(() => {
    if (isRunningStandalone() || isAppInstalled) {
      document.body.classList.add('standalone');
      // Hide the install message if the app is installed
      setShowInstallMessage(false);
    } else {
      document.body.classList.remove('standalone');
    }
  }, [isAppInstalled]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box 
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
        }}
      >
        {/* Main content */}
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/quiz/:quizId" element={<Quiz />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>

        {/* Network status indicator */}
        <NetworkStatusIndicator />

        {/* PWA Install Prompt */}
        {showInstallMessage && (
          <Box 
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: 'background.paper',
              boxShadow: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: (theme) => theme.zIndex.snackbar,
            }}
          >
            <Typography variant="body1">
              Install this app for a better experience
            </Typography>
            <InstallButton 
              deferredPrompt={deferredPrompt} 
              onInstall={() => setShowInstallMessage(false)}
              variant="contained"
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default App;
